/**
 * Save integrity — HMAC checksum computation and verification.
 *
 * # The threat model
 *
 * Save corruption falls into four categories. This layer detects all four:
 *
 *   1. **Tampering** — a player opens DevTools, edits the IndexedDB blob to
 *      give themselves 999 quantumSigils, and reloads. The edited bytes
 *      hash to a different checksum than the stored one. Verification fails.
 *
 *   2. **Cross-install copying** — Player A copies their save file and
 *      sends it to Player B to "give them progress." Player B drops it
 *      into their IndexedDB. Player A's save was signed with Player A's
 *      install secret; Player B's verification uses Player B's install
 *      secret; the checksums don't match. Verification fails.
 *
 *   3. **Silent corruption** — extremely rare browser/disk bit-flips.
 *      Even one bit changed in the canonical bytes produces a different
 *      hash. Verification fails. Phase B can fall back to a backup save.
 *
 *   4. **Substitution attacks** — replacing a current save with an older
 *      save from the same install (the secret is the same, so naive HMAC
 *      would still verify). Caught because the canonical bytes include
 *      `createdAt`, so two saves at different times produce different
 *      checksums even with the same install secret.
 *
 * # What we DON'T defend against
 *
 *   - **Server-authoritative anti-cheat.** Local HMAC catches casual
 *     tampering but a determined attacker can extract the install secret
 *     from IndexedDB and re-sign a tampered save. This is fine for the
 *     Phase A threat model (preventing accidental corruption + casual
 *     cheating in solo modes). For competitive content (leaderboards,
 *     Sigil purchases), Stage 1+ uses server-authoritative validation.
 *
 *   - **Replay attacks across installs.** If a player exports their save
 *     and re-imports it later, the same envelope verifies. This is by
 *     design — saves are meant to be re-loadable.
 *
 * # Provider abstraction (browser-safe by design)
 *
 * This file is browser-safe — it imports nothing Node-specific. The
 * `HmacProvider` and `InstallSecretSource` interfaces define the contract;
 * implementations live in separate files and are injected at the call site:
 *
 *   - `nodeHmacProvider` and `makeInMemoryInstallSecret` live in
 *     `integrity.node.ts` and use `node:crypto`. Imported by tests only.
 *
 *   - Phase B will ship `browserHmacProvider` and an IndexedDB-backed
 *     install secret source in `packages/ui/src/persistence/`. Those
 *     consume browser primitives (crypto.subtle or noble-hashes; IndexedDB
 *     directly).
 *
 * The save layer doesn't know or care which provider is wired in. This
 * isolation is what lets the production browser bundle build cleanly —
 * `integrity.ts` itself never reaches for Node-only modules.
 *
 * @see {@link ./serialize.ts} — canonical bytes feed into HMAC input
 * @see {@link ./schema.ts} — checksum field is 64-char lowercase hex
 * @see {@link ./integrity.node.ts} — Node-backed provider for tests
 */

import {
  SaveEnvelopeSchema,
  type SaveEnvelope,
  type SaveEnvelopeForChecksum,
} from './schema';
import { canonicalize } from './serialize';

// ─────────────────────────────────────────────────────────────────────────────
// Errors
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Thrown by `verifyChecksum` when the stored checksum doesn't match a
 * recomputed one. Phase B catches this and maps to
 * `SaveLoadFailureReason: 'tamper_detected'`.
 *
 * NOT thrown for missing/malformed checksum field — that's a schema
 * validation problem caught at deserialize time.
 */
export class IntegrityError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'IntegrityError';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// HmacProvider — pluggable HMAC implementation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute and verify HMAC-SHA256 over byte sequences.
 *
 * Phase A ships a Node implementation in `integrity.node.ts`; Phase B will
 * ship a browser implementation in `packages/ui/src/persistence/`. Same
 * interface either way.
 *
 * Both methods are synchronous so they compose naturally with the synchronous
 * serialize layer. The browser implementation will use a sync HMAC primitive
 * (e.g., `noble-hashes`, ~5KB minified) since `crypto.subtle.sign` is async.
 * Phase B chooses the library; this interface is the contract.
 */
export interface HmacProvider {
  /** Compute HMAC-SHA256 over the message. Returns lowercase hex. */
  sign(key: Uint8Array, message: Uint8Array): string;

  /** Verify HMAC-SHA256. Returns true if `expected` matches. Timing-safe. */
  verify(key: Uint8Array, message: Uint8Array, expected: string): boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// InstallSecretSource — pluggable install secret abstraction
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Returns the per-install secret used to sign HMACs. Phase A ships an
 * in-memory test impl in `integrity.node.ts`. Phase B ships an
 * IndexedDB-backed impl that generates and persists 32 random bytes on
 * first launch.
 *
 * # Why a function, not a value
 *
 * Browser-side persistent secret retrieval may need to await IndexedDB.
 * The function signature accommodates async via a sync wrapper at Phase B's
 * adapter boundary (the boundary will pre-load the secret on app boot,
 * making the runtime call synchronous).
 *
 * # Why not derive from installId
 *
 * The schema's `installId` is a UUID v4 — random but PUBLIC (it's part of
 * the envelope, visible to anyone with the save file). The HMAC SECRET must
 * not be derivable from public data. Phase B generates a separate 32-byte
 * random secret and stores it in IndexedDB. The install ID identifies
 * which install; the secret authenticates that install.
 */
export interface InstallSecretSource {
  /** Return the 32-byte HMAC key for this install. */
  getSecret(): Uint8Array;
}

// ─────────────────────────────────────────────────────────────────────────────
// Checksum computation
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Compute the HMAC-SHA256 checksum for a pre-checksum envelope.
 *
 * Process:
 *   1. canonicalize() the envelope (recursive sorted keys)
 *   2. JSON.stringify the canonical form
 *   3. UTF-8 encode the resulting string to bytes
 *   4. HMAC-SHA256(secret, bytes)
 *   5. Hex-encode the digest (64 lowercase chars)
 *
 * Pure function. Same envelope + same secret -> same hex.
 */
export function computeChecksum(
  envelope: SaveEnvelopeForChecksum,
  secretSource: InstallSecretSource,
  hmacProvider: HmacProvider,
): string {
  const canonical = canonicalize(envelope);
  const json = JSON.stringify(canonical);
  const bytes = new TextEncoder().encode(json);
  const secret = secretSource.getSecret();
  return hmacProvider.sign(secret, bytes);
}

/**
 * Attach a freshly-computed checksum to a pre-checksum envelope.
 *
 * Returns a complete `SaveEnvelope` ready for `envelopeToJson`.
 * The returned envelope is validated against `SaveEnvelopeSchema`.
 */
export function attachChecksum(
  envelope: SaveEnvelopeForChecksum,
  secretSource: InstallSecretSource,
  hmacProvider: HmacProvider,
): SaveEnvelope {
  const checksum = computeChecksum(envelope, secretSource, hmacProvider);
  const complete: SaveEnvelope = { ...envelope, checksum };
  // Validate the assembled envelope shape (schema-level sanity check).
  const result = SaveEnvelopeSchema.safeParse(complete);
  if (!result.success) {
    throw new IntegrityError(
      `attached envelope failed schema validation: ${result.error.message}`,
    );
  }
  return result.data;
}

// ─────────────────────────────────────────────────────────────────────────────
// Checksum verification
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Verify the checksum on a complete envelope.
 *
 * Throws `IntegrityError` if the checksum does NOT match. Returns the same
 * envelope (unchanged) if it does match — convenient for chaining
 * (e.g., `applyState(verifyChecksum(deserialize(json), secret, hmac))`).
 *
 * Comparison is timing-safe via the HMAC provider's `verify` method.
 *
 * # The math
 *
 * To verify, we strip the checksum field, recompute checksum over the
 * stripped envelope, and timing-safe compare to the stored checksum.
 * If the stripped envelope's bytes differ even by one bit from the
 * original signing input, the recomputed checksum differs and verification
 * fails.
 */
export function verifyChecksum(
  envelope: SaveEnvelope,
  secretSource: InstallSecretSource,
  hmacProvider: HmacProvider,
): SaveEnvelope {
  // Strip checksum to recompute.
  const { checksum: storedChecksum, ...preChecksum } = envelope;
  const canonical = canonicalize(preChecksum);
  const json = JSON.stringify(canonical);
  const bytes = new TextEncoder().encode(json);
  const secret = secretSource.getSecret();
  const ok = hmacProvider.verify(secret, bytes, storedChecksum);
  if (!ok) {
    throw new IntegrityError(
      'checksum mismatch (tampered, cross-install, or corrupt save)',
    );
  }
  return envelope;
}

// ─────────────────────────────────────────────────────────────────────────────
// Re-exports for ergonomics
// ─────────────────────────────────────────────────────────────────────────────

export type { SaveEnvelope, SaveEnvelopeForChecksum };
